import React, { FormEvent, useState } from 'react'
import { GridItem, GridContainer } from '@entur/grid'
import { space } from '@entur/tokens'
import { Heading1, Heading2 } from '@entur/typography'
import { PrimaryButton, SecondaryButton } from '@entur/button'
import { useNavigate } from 'react-router'
import { SmallAlertBox } from '@entur/alert'
import { BackButton } from '../components/layout/back-button'
import { useApi } from '../api-clients/client-hooks'
import { postEmployee } from '../api-clients/eba-clients'
import { fieldsToObject, formIsValid, validateAllFields, ValidationFormField } from '../utils/form-utils'
import { FetchState } from '../api-clients/api-types'
import { ModalErrorAlert } from '../components/modal/modal-error-alert'
import { ssnErrorMessage } from '../utils/ssn-utils'
import CompanyDropdown, { companyFormField } from '../components/form/company-dropdown'
import {
  dateOfBirthFormField,
  addressFormField,
  employeeNoFormField,
  employmentPercentageFormField,
  employmentStartDateFormField,
  employmentTypeFormField,
  firstNameFormField,
  hireStartDateFormField,
  paidPercentageFormField,
  payTypeFormField,
  postalCodeFormField,
  surnameFormField,
} from '../utils/common-form-fields'
import { EmploymentTypeDropdown, PaymentTypeDropdown } from '../components/form/dropdowns'
import { ValidatableTextField } from '../components/form/validatable-text-field'
import ValidatableDatePicker from '../components/form/validatable-date-picker'
import { ssnField, SsnFormField } from '../components/form/ssn-form-field'

type State = {
  firstName: ValidationFormField<string>
  surname: ValidationFormField<string>
  dateOfBirth: ValidationFormField<Date | undefined>
  ssn: ValidationFormField<string | undefined>
  address: ValidationFormField<string>
  postalCode: ValidationFormField<string>
  company: ValidationFormField<number | undefined>
  employeeNo: ValidationFormField<string>
  employmentType: ValidationFormField<string | undefined>
  hireStartDate: ValidationFormField<Date | undefined>
  employmentPercentage: ValidationFormField<string>
  paidPercentage: ValidationFormField<string>
  employmentStartDate: ValidationFormField<Date | undefined>
  payType: ValidationFormField<string | undefined>
}

function initialState(): State {
  return {
    firstName: firstNameFormField('', true),
    surname: surnameFormField('', true),
    dateOfBirth: dateOfBirthFormField(undefined),
    ssn: ssnField(undefined),
    address: addressFormField('', true),
    postalCode: postalCodeFormField('', true),
    company: companyFormField(undefined, true),
    employeeNo: employeeNoFormField(undefined, true),
    employmentType: employmentTypeFormField(undefined, true),
    hireStartDate: hireStartDateFormField(undefined, true),
    employmentPercentage: employmentPercentageFormField(undefined),
    paidPercentage: paidPercentageFormField(undefined),
    employmentStartDate: employmentStartDateFormField(undefined, false),
    payType: payTypeFormField(undefined, true),
  }
}

const NewEmployeePage: React.FC = () => {
  const navigate = useNavigate()
  const [isValid, setValid] = useState(true)
  const [ssnError, setSsnError] = useState<string>()
  const [postResponse, doPost] = useApi(postEmployee, {
    successCallback: (data) => {
      navigate(`/ansatt/${data.id}#ny`)
    },
    errorCallback: (error) => {
      setSsnError(ssnErrorMessage(error))
    },
  })
  const [state, setState] = useState(initialState())

  const goBack = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    navigate('/')
  }

  const createEmployee = (e: FormEvent) => {
    e.preventDefault()
    const localState = validateAllFields(state)
    const valid = formIsValid(localState)
    if (valid) {
      setValid(true)
      doPost({
        body: fieldsToObject(state, { includeSenstive: true }),
      })
    } else {
      setState(localState)
      setValid(false)
    }
  }

  return (
    <>
      <BackButton />

      <form onSubmit={createEmployee} style={{ marginTop: space.extraLarge2 }}>
        <GridContainer>
          <GridItem small={12} medium={12} large={10}>
            <Heading1>Ny rettighetseier</Heading1>

            <Heading2>Person</Heading2>
            <GridContainer spacing="medium">
              <GridItem small={3}>
                <ValidatableTextField
                  field={state.firstName}
                  update={(e) =>
                    setState({
                      ...state,
                      firstName: e,
                    })
                  }
                />
              </GridItem>

              <GridItem small={3}>
                <ValidatableTextField
                  field={state.surname}
                  update={(e) =>
                    setState({
                      ...state,
                      surname: e,
                    })
                  }
                />
              </GridItem>

              <GridItem small={3}>
                <ValidatableDatePicker
                  date={state.dateOfBirth}
                  update={(e) => setState({ ...state, dateOfBirth: e })}
                />
              </GridItem>
              <GridItem small={3}>
                <SsnFormField
                  dateOfBirthField={state.dateOfBirth}
                  ssnField={state.ssn}
                  updateSsn={(e) =>
                    setState({
                      ...state,
                      ssn: e,
                    })
                  }
                />
              </GridItem>

              <GridItem small={3}>
                <ValidatableTextField
                  field={state.address}
                  update={(e) =>
                    setState({
                      ...state,
                      address: e,
                    })
                  }
                />
              </GridItem>

              <GridItem small={3}>
                <ValidatableTextField
                  field={state.postalCode}
                  update={(e) =>
                    setState({
                      ...state,
                      postalCode: e,
                    })
                  }
                />
              </GridItem>
            </GridContainer>

            <Heading2 style={{ marginTop: space.extraLarge3 }}>Selskap</Heading2>
            <GridContainer spacing="medium">
              <GridItem small={4}>
                <CompanyDropdown
                  placeholder="Velg selskap"
                  required
                  company={state.company}
                  update={(e) =>
                    setState({
                      ...state,
                      company: e,
                    })
                  }
                />
              </GridItem>

              <GridItem small={3}>
                <EmploymentTypeDropdown
                  field={state.employmentType}
                  update={(e) =>
                    setState({
                      ...state,
                      employmentType: e,
                    })
                  }
                />
              </GridItem>

              <GridItem small={5}>
                <GridContainer spacing="extraSmall2">
                  <GridItem small={5}>
                    <ValidatableTextField
                      field={state.employeeNo}
                      update={(e) =>
                        setState({
                          ...state,
                          employeeNo: e,
                        })
                      }
                    />
                  </GridItem>

                  <GridItem small={7}>
                    <ValidatableDatePicker
                      date={state.hireStartDate}
                      update={(e) =>
                        setState({
                          ...state,
                          hireStartDate: e,
                        })
                      }
                    />
                  </GridItem>
                </GridContainer>
              </GridItem>

              <GridItem small={4}>
                <PaymentTypeDropdown
                  field={state.payType}
                  update={(e) =>
                    setState({
                      ...state,
                      payType: e,
                    })
                  }
                />
              </GridItem>

              <GridItem small={2}>
                <ValidatableTextField
                  field={state.employmentPercentage}
                  update={(e) =>
                    setState({
                      ...state,
                      employmentPercentage: e,
                    })
                  }
                />
              </GridItem>
              <GridItem small={2}>
                <ValidatableTextField
                  field={state.paidPercentage}
                  update={(e) =>
                    setState({
                      ...state,
                      paidPercentage: e,
                    })
                  }
                />
              </GridItem>
              <GridItem small={4}>
                <ValidatableDatePicker
                  date={state.employmentStartDate}
                  update={(e) =>
                    setState({
                      ...state,
                      employmentStartDate: e,
                    })
                  }
                />
              </GridItem>
            </GridContainer>
          </GridItem>

          <GridItem small={12} medium={12} large={2} />

          <GridItem small={6} style={{ marginTop: space.extraLarge9 }}>
            {postResponse.status === FetchState.ERROR && !ssnError && (
              <ModalErrorAlert title="Får ikke opprettet ny rettighetseier" />
            )}
            {ssnError && (
              <SmallAlertBox variant="error" width="fit-content" style={{ marginBottom: space.large }}>
                {ssnError}
              </SmallAlertBox>
            )}
            {!isValid && (
              <SmallAlertBox variant="error" width="fit-content" style={{ marginBottom: space.large }}>
                Alle felt må være fylt ut med gyldige verdier for å sende inn skjemaet.
              </SmallAlertBox>
            )}

            <PrimaryButton
              className="spacing-right-medium"
              type="submit"
              loading={postResponse.status === FetchState.LOADING}
              disabled={postResponse.status === FetchState.LOADING}
            >
              Lagre rettighetseier
            </PrimaryButton>
            <SecondaryButton onClick={goBack} disabled={postResponse.status === FetchState.LOADING}>
              Forkast rettighetseier
            </SecondaryButton>
          </GridItem>
        </GridContainer>
      </form>
    </>
  )
}

export default NewEmployeePage
